/**
 * Cancellation & Refund Policy — Revision 2.0, effective July 28, 2026.
 *
 * The CLIENT'S authoritative text (Kidhive_Cancellation_and_Refund_Policy_v2.0.pdf), transcribed
 * verbatim. ONE platform-wide policy. The refund engine's tiers mirror sec.3 (100% at 24h+, 50%
 * at 12-24h, none under 12h — src/util/cancellationPolicy.js + the server mirror) and the
 * reschedule cutoff mirrors sec.6 (the full-refund window, 24h). Change the numbers here and there
 * TOGETHER (and bump the version in src/config/legal.js + server/api-util/legalVersions.js).
 *
 * v2.0 changes from v1.9: rewritten in the third person ("a parent"/"the parent" rather than "you")
 * throughout, and sec.1 now states classes can be booked up to 6 months in advance (measured against
 * the first session for camps and full-term enrollments). The refund/reschedule mechanics are
 * unchanged, so no code or handover-doc change is needed (client confirmed).
 */
const content = [
  {
    type: 'lead',
    text:
      "This policy explains how cancellations, refunds, and rescheduling work when a parent books a children's activity through KidHive. It applies alongside KidHive's Terms of Service and Provider Agreement; where a term is defined there, it means the same here. Questions? Email info@KidHive.co.",
  },

  { type: 'h2', text: '1. How bookings and payments work' },
  {
    type: 'p',
    text:
      "Joining KidHive is free for both parents and Providers. Parents can book three kinds of activities — single drop-in sessions, camps, and full-term (multi-week) enrollments — and pay for them directly in the KidHive app using Apple Pay, Google Pay, or a card through KidHive's payment processor, Stripe. KidHive is **not** the merchant of record. When a parent pays, the funds go to the Provider running the activity; the Provider bears the Stripe processing fee and KidHive's platform fee out of that amount. Classes can be booked up to 6 months in advance; for camps and full-term enrollments this is measured against the first session.",
  },

  { type: 'h2', text: '2. One platform-wide cancellation policy' },
  {
    type: 'p',
    text:
      'To keep things simple and consistent for everyone, KidHive has a **single, platform-wide cancellation policy** — the one described below. Every Provider accepts it when they register to list on KidHive, and every parent accepts it when they book. Parents also see the policy again at checkout before confirming a booking.',
  },
  {
    type: 'p',
    text:
      'The version of the policy in effect at the time of booking is the one that applies to that booking, even if the policy changes later. Changes apply only to new bookings, never to bookings already made, and KidHive will notify parents and Providers by email of any change.',
  },

  { type: 'h2', text: '3. Single drop-in sessions' },
  {
    type: 'p',
    text:
      "When a parent cancels a single-session booking, the system automatically calculates and issues the refund based on how far ahead of the session's local start time the parent cancels:",
  },
  {
    type: 'ul',
    items: [
      '**24 hours or more before the start:** 100% refund.',
      '**Between 12 and 24 hours before the start:** 50% refund.',
      '**Less than 12 hours before the start:** no refund (non-refundable).',
    ],
  },
  {
    type: 'p',
    text: "The parent does not need the Provider to approve the refund — it's handled automatically.",
  },

  { type: 'h2', text: '4. Camps' },
  {
    type: 'ul',
    items: [
      "**Any time before the camp's first session:** 100% refund.",
      '**On or after the first session:** no refund (non-refundable).',
    ],
  },

  { type: 'h2', text: '5. Full-term (multi-week) enrollments' },
  {
    type: 'p',
    text:
      'A full-term (multi-week) enrollment is paid in full, upfront, at booking — the same way a camp is. The same cancellation rule applies:',
  },
  {
    type: 'ul',
    items: [
      "**Any time before the enrollment's first session:** 100% refund.",
      '**On or after the first session:** no refund (non-refundable).',
    ],
  },

  { type: 'h2', text: '6. Rescheduling' },
  {
    type: 'p',
    text:
      'For single-session bookings, a parent can reschedule into another session of the same class **as long as the booking is still within the full-refund window** (24 hours or more before the session start) — the same timing that would allow the parent to cancel for a full refund. Outside that window, the parent would need to cancel (subject to the rules above) and rebook. Rescheduling is not currently supported for camps or full-term (multi-week) enrollments.',
  },
  {
    type: 'p',
    text:
      "**Camps and full-term (multi-week) enrollments.** KidHive doesn't support rescheduling a camp or full-term enrollment. A parent who would like to move to a different one before it starts should cancel the current booking — which qualifies for a 100% refund at that point — and book the desired one directly; the new booking isn't guaranteed and is subject to availability. Once a camp or enrollment has started, the standard cancellation rule applies (no refund), so this isn't an option at that point. Canceling or rescheduling individual days within a camp or full-term enrollment is not supported at this time, for either the parent or the Provider.",
  },
  {
    type: 'p',
    text:
      "**Arranging directly with a Provider.** For make-up sessions, or any rescheduling KidHive doesn't support, parents are welcome to contact the Provider directly to work something out — there is no need to change the booking on KidHive to do so. These are private arrangements between the parent and the Provider: KidHive isn't involved, and KidHive's automated cancellation and refund protections don't apply to them. Any new or additional paid booking should still be made through KidHive so the parent's payment and refund protections stay in place.",
  },

  { type: 'h2', text: '7. When a Provider cancels or reschedules' },
  {
    type: 'p',
    text:
      "**Canceling a single drop-in session.** If a Provider cancels a booking, the parent receives a **100% refund** of the original charge regardless of the standard rules above, and KidHive reverses its platform fee so the parent is made whole and the Provider isn't charged KidHive's fee on that booking. The Provider is responsible for the Stripe processing fee on the refunded booking.",
  },
  {
    type: 'p',
    text:
      '**Rescheduling a single drop-in session.** A Provider may reschedule a single drop-in session for any reason (for example, an instructor becomes unavailable). The parent is notified of the proposed new date/time and can either accept or reject it:',
  },
  {
    type: 'ul',
    items: [
      '**If the parent accepts:** no money is transacted — the booking simply moves to the new date/time.',
      '**If the parent rejects:** the parent receives a **100% refund** and KidHive reverses its platform fee; the Provider is responsible for the Stripe processing fee.',
    ],
  },
  {
    type: 'p',
    text:
      '**Camps and full-term (multi-week) enrollments.** Providers cannot reschedule a camp or full-term enrollment at this time. A Provider who wants to move one to a new date/time must cancel it and republish it as a new listing; a Provider who simply wants to cancel does so from the Provider dashboard. In each case, every affected parent receives a **100% refund**, KidHive reverses its platform fee, and the Provider is responsible for the Stripe processing fee if the camp/full-term had an active booking at the time of the change.',
  },
  {
    type: 'p',
    text:
      '**Frequent cancellations or reschedules.** Providers are expected to honor the sessions they publish. A Provider who cancels or reschedules classes often may have their Provider account suspended.',
  },

  { type: 'h2', text: '8. When KidHive cancels' },
  {
    type: 'p',
    text:
      'If KidHive has to cancel a class or session for any reason — such as a suspension of a Provider account for breach of agreement, or on behalf of the Provider — the parent receives a **100% refund** of the original charge and KidHive reverses its platform fee. As with every cancellation on KidHive, the Provider remains responsible for the Stripe processing fee, regardless of who initiated the cancellation.',
  },

  { type: 'h2', text: '9. Force majeure and exceptional cases' },
  {
    type: 'p',
    text:
      "In exceptional circumstances — emergencies, closures, or other events outside anyone's control — KidHive may apply an administrative override to issue a 100% refund regardless of the standard rules above. In these force-majeure cancellations the parent is refunded in full and KidHive reverses its platform fee; the Provider remains responsible for the Stripe processing fee, as with a Provider-initiated cancellation.",
  },

  { type: 'h2', text: '10. How refunds are issued' },
  {
    type: 'p',
    text:
      "Refunds go back to the parent's original payment method through Stripe. The time a refund takes to appear depends on the parent's bank or card issuer, typically several business days. Partial refunds recalculate the platform fee proportionally, so each party is settled correctly.",
  },

  { type: 'h2', text: '11. Problems with a booking or refund' },
  {
    type: 'p',
    text:
      "If something goes wrong — an unrecognized charge, a refund that didn't arrive, or a dispute with a Provider about a cancellation — parents can contact KidHive at info@KidHive.co. KidHive will review the issue and help facilitate a resolution. While KidHive provides the booking and refund system, the underlying activity is the Provider's, and the platform-wide cancellation terms above are the ones the parent agreed to at booking.",
  },

  { type: 'h2', text: '12. Contact' },
  { type: 'contact', lines: ['KidHive, LLC.', 'info@KidHive.co'] },
];

export default content;
